import AuthData from "lib/authData";
import { hasAnalyticsConsent } from "./consent";

/**
 * The pushed event name is intentionally kept as "amplitudeIdentify": the live GTM
 * "Amplitude Identity" trigger keys on it and fans out to BOTH Amplitude and the
 * Decibel Insight (Medallia DXA) custom-dimensions tag. Renaming the string would
 * need GTM coordination. Only the function/file name is generic.
 */
const IDENTIFY_EVENT = "amplitudeIdentify";

/**
 * Checks if the identify event has already been sent in the current GTM session
 * by checking the dataLayer history for previous identify events.
 */
const hasBeenSentThisSession = (): boolean => {
  const dataLayer = window.dataLayer;
  if (!dataLayer) {
    return false;
  }
  return dataLayer.some((item) => item.event === IDENTIFY_EVENT);
};

/**
 * Pushes the user-identity event to the GTM dataLayer with user properties. GTM
 * routes it to all configured providers (Amplitude + DXA). Only sends if:
 * - Analytics cookie consent has been granted
 * - User is logged in
 * - Event hasn't been sent this session
 *
 * The raw user id is intentionally NOT included in the payload: it is used only
 * as a login gate. GTM/Amplitude/DXA receive non-identifying segmentation
 * properties (role, companyId) only — the individual is never identified.
 */
const sendUserIdentify = (): void => {
  // Check if already sent this session
  if (hasBeenSentThisSession()) {
    return;
  }

  // Check cookie consent
  if (!hasAnalyticsConsent()) {
    return;
  }

  // Get user data from localStorage
  const authData = AuthData.get();
  if (!authData || !authData.user) {
    return;
  }

  const { user } = authData;

  // Login gate only — user.id is never transmitted, just used to confirm a
  // real logged-in user before identifying.
  if (!user.id) {
    return;
  }

  // Build userProperties object
  const userProperties: Record<string, any> = {};

  if (user.role) {
    userProperties.role = user.role;
  }

  if (user.companyId) {
    userProperties.companyId = user.companyId;
  }

  window.dataLayer?.push({
    event: IDENTIFY_EVENT,
    userProperties,
  });
};

export default sendUserIdentify;
